import { useAuth0 } from '@auth0/auth0-react';
import { Outlet, useRouter, useSearch } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense, useState } from 'react';
import { useSdkTokenQuery } from '@/api/queries/useSdkTokenQuery';
import { useThoughtSpotBannerQuery } from '@/api/queries/useThoughtSpotBannerQuery';
import MaintenanceBanner from '@/components/Thoughtspot/MaintenanceBanner';
import { useThoughtSpotAuth } from '@/hooks/thoughtspot/visual-sdk/useThoughtSpotAuth';
import { useDatacenterInit } from '@/hooks/useDatacenterInit';
import { useScheduledMaintenanceBanner } from '@/hooks/useScheduledMaintenanceBanner';
import { useBoundStore } from '@/store';
import { AppLayout } from './AppLayout';
import { LoadingView } from './LoadingView';
import { RootErrorView } from './RootErrorView';
import type { RootErrorDescriptor, TSEventError } from './types';

export function RootLayout() {
  // State Hooks
  const [tsEventError, setTSEventError] = useState<TSEventError | null>(null);
  useThoughtSpotAuth({ setTSEventError });

  // Router
  const router = useRouter();
  // Validated by the root route's `validateSearch` — already narrowed to a known Region or
  // undefined, so no unsafe cast is needed here.
  const { datacenter } = useSearch({ from: '__root__' });

  // Initialize datacenter configuration on mount
  useDatacenterInit(datacenter);

  // Context (Auth0)
  const { isAuthenticated, isLoading: isLoadingAuth } = useAuth0();

  // Zustand - only client-side state
  const { authStatus, isRegionTransitioning } = useBoundStore();

  /**
   * SDK token query: authenticates ThoughtSpot SDK
   * Enabled only after:
   * 1. Auth0 fully loads
   * 2. Region is not transitioning (datacenter init complete)
   */
  const {
    status: sdkTokenStatus,
    error: sdkTokenResponseError,
    refetch: fetchSdkToken,
  } = useSdkTokenQuery({ enabled: isAuthenticated && !isLoadingAuth && !isRegionTransitioning });

  /** ThoughtSpot maintenance banner status (live "in progress" outage) */
  const { data: bannerData } = useThoughtSpotBannerQuery({ isAuthenticated, authStatus });

  /** Manually-scheduled advance-warning banner (Unleash flag, set from TS's email) */
  const scheduledBanner = useScheduledMaintenanceBanner();

  const handleRetry = () => {
    void fetchSdkToken();
  };
  const handleRetryTSError = () => {
    setTSEventError(null);
    void fetchSdkToken();
  };

  // Derive UI state flags
  const showGlobalLoader = isLoadingAuth || (sdkTokenStatus === 'pending' && !sdkTokenResponseError);
  const isUnderMaintenance = bannerData?.under_maintenance === true;
  const maintenanceMessage = bannerData?.banner_text || '';

  const activeError: RootErrorDescriptor | null = tsEventError
    ? {
        status: tsEventError.status,
        message: `Connection error: ${tsEventError.message} (${tsEventError.errorType})`,
        onRetry: handleRetryTSError,
        label: 'Retry Connection',
      }
    : sdkTokenResponseError
      ? {
          status: sdkTokenResponseError.status || 500,
          message: sdkTokenResponseError?.data?.message,
          onRetry: handleRetry,
          label: 'Retry',
        }
      : null;

  // Loader takes precedence over error states during initial load.
  if (!showGlobalLoader && activeError) {
    return <RootErrorView {...activeError} />;
  }

  // AppLayout (and therefore TopHeader) stays mounted at a stable position across
  // the loader -> content transition; only the <main> body swaps. This avoids
  // remounting TopHeader when showGlobalLoader flips (e.g. when the SDK token
  // resolves), which previously reset the shared-nav fetch and caused the
  // Create button / waffle menu to flicker and refetch.
  return (
    <>
      {isUnderMaintenance ? (
        // Live outage in progress — service is degraded, so keep it non-dismissible.
        <MaintenanceBanner
          key="in-progress"
          message={maintenanceMessage}
          dismissible={false}
        />
      ) : scheduledBanner.isActive ? (
        // Advance heads-up for a scheduled outage — informational, so allow dismissal.
        // Distinct `key` keeps the banner's internal dismiss state from bleeding
        // into the in-progress banner if the live outage later begins.
        <MaintenanceBanner
          key="scheduled"
          message={scheduledBanner.message}
          dismissible
        />
      ) : null}
      <AppLayout>
        <Suspense fallback={<LoadingView />}>{showGlobalLoader ? <LoadingView /> : <Outlet />}</Suspense>
        <TanStackRouterDevtools router={router} />
      </AppLayout>
    </>
  );
}
